import { assoc, concat, mergeDeepWithKey } from 'ramda';
import shortid from 'shortid';
import { isArray } from '../../../Utils/ramdaCookbook';
import {
  IFieldError,
  IFieldWarning,
  IErrors,
  IWarnings,
  IFormIssues,
} from '../../../Types/FormTypes';

/**
 * if fieldIssue contains any issues it will be added to formIssues as issues on the field with name fieldName
 * @param fieldName name of the field
 * @param fieldIssues
 * @param formIssues
 */
export const addFieldIssuesToFormIssues = <T extends IErrors | IWarnings>(
  fieldName: string,
  fieldIssues: IFieldError[] | IFieldWarning[] | undefined,
  formIssues: T
): T => {
  if (fieldIssues == null || fieldIssues.length === 0) {
    return formIssues;
  } else {
    return assoc(fieldName, fieldIssues, formIssues);
  }
};

/**
 * Creates a new field error with provided message
 * @param message
 */
export const createNewFieldError = (message: string): IFieldError => ({
  id: shortid.generate(),
  message: message,
});

/**
 * Creates a new field warning with provided message
 * @param message
 */
export const createNewFieldWarning = (message: string): IFieldWarning => ({
  id: shortid.generate(),
  message: message,
});

/**
 * Merges the left and rightvalue, if values are an array use concat to merge them, otherwise use the right value
 */
const formIssuesMergeFn = (key: string, leftValue: any, rightValue: any) => {
  if (isArray(leftValue) && isArray(rightValue)) {
    return concat(leftValue, rightValue);
  } else {
    return rightValue;
  }
};

/**
 * Creates a new issues object with the own properties of the first object merged with
 * the own properties of the second object. If a key exists in both objects:
 * - and both values are objects, the two values will be recursively merged
 * - otherwise if both values are arrays, the two values will be concated
 * - otherwise the value from the second object will be used.
 *
 * @param leftIssues first issues object
 * @param rightIssues second issues object
 */
export const mergeFormIssues = (
  leftIssues: IFormIssues,
  rightIssues: IFormIssues
): IFormIssues => {
  return mergeDeepWithKey(formIssuesMergeFn, leftIssues, rightIssues);
};
